/**
 * Shared presentational components.
 *
 * Deliberately small and unabstracted — a contributor should be able to read any
 * one of these in ten seconds. Anything that needs configuration takes explicit
 * props rather than a variants object.
 */

import type { ReactNode } from 'react'
import { AlertTriangle, CheckCircle2, Info, XCircle } from 'lucide-react'
import type { Severity } from '@/types/resume'

export const scoreTone = (score: number): 'success' | 'warning' | 'error' =>
  score >= 78 ? 'success' : score >= 55 ? 'warning' : 'error'

const TONE_STROKE: Record<string, string> = {
  success: 'var(--color-success)',
  warning: 'var(--color-warning)',
  error: 'var(--color-error)',
}

/**
 * The headline score. An SVG ring rather than a progress bar because the number
 * must be legible at a glance on mobile, and because the ring reads as a "grade"
 * rather than "progress toward completion" — which is the wrong mental model for
 * an ATS score.
 */
export const ScoreRing = ({
  score,
  label,
  size = 132,
  sublabel,
  displayScore,
}: {
  score: number
  label?: string
  size?: number
  sublabel?: string
  /**
   * The number to *draw*, when it differs from the number that is true.
   *
   * Lets a caller animate the figure without this module importing an animation
   * library — `ui.tsx` is shared with every prerendered page and must stay free
   * of runtime it does not need. `score` remains the accessible value, so the
   * `aria-label` states the real result rather than whatever frame the tween is
   * on.
   */
  displayScore?: number
}) => {
  const drawn = displayScore ?? score
  const radius = (size - 14) / 2
  const circumference = 2 * Math.PI * radius
  const offset = circumference * (1 - Math.max(0, Math.min(100, drawn)) / 100)
  const tone = scoreTone(score)

  return (
    <div
      className="score-ring relative inline-flex items-center justify-center"
      style={{ width: size, height: size }}
      role="img"
      aria-label={`${label ?? 'Score'}: ${score} out of 100`}
    >
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="var(--color-base-300)"
          strokeWidth="9"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={TONE_STROKE[tone]}
          strokeWidth="9"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>
      <div className="absolute flex flex-col items-center leading-none">
        <span className="text-3xl font-bold tabular-nums">{drawn}</span>
        {sublabel && <span className="mt-1 text-[0.65rem] uppercase tracking-wide opacity-70">{sublabel}</span>}
      </div>
    </div>
  )
}

export const ScoreBar = ({
  value,
  max,
  label,
}: {
  value: number
  max: number
  label: string
}) => {
  const pct = max === 0 ? 0 : Math.round((value / max) * 100)
  return (
    <div>
      <div className="mb-1 flex items-baseline justify-between gap-3 text-sm">
        <span className="font-medium">{label}</span>
        <span className="tabular-nums opacity-70">
          {value}/{max}
        </span>
      </div>
      <div
        className="h-2 w-full overflow-hidden rounded-full bg-base-300"
        role="progressbar"
        aria-valuenow={pct}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={label}
      >
        <div
          className={`h-full rounded-full bg-${scoreTone(pct)}`}
          style={{ width: `${pct}%`, background: TONE_STROKE[scoreTone(pct)] }}
        />
      </div>
    </div>
  )
}

const SEVERITY_ICON: Record<Severity, typeof Info> = {
  critical: XCircle,
  warning: AlertTriangle,
  info: Info,
  pass: CheckCircle2,
}

const SEVERITY_CLASS: Record<Severity, string> = {
  critical: 'text-error',
  warning: 'text-warning',
  info: 'text-info',
  pass: 'text-success',
}

export const SeverityIcon = ({ severity, className = '' }: { severity: Severity; className?: string }) => {
  const Icon = SEVERITY_ICON[severity]
  return <Icon className={`${SEVERITY_CLASS[severity]} ${className}`} size={18} aria-hidden="true" />
}

export const SEVERITY_LABEL: Record<Severity, string> = {
  critical: 'Critical',
  warning: 'Warning',
  info: 'Suggestion',
  pass: 'Passing',
}

export const Card = ({
  title,
  description,
  action,
  children,
  id,
}: {
  title?: ReactNode
  description?: ReactNode
  action?: ReactNode
  children: ReactNode
  id?: string
}) => (
  <section id={id} className="rounded-box border border-base-300 bg-base-100 p-4 sm:p-5">
    {(title || action) && (
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          {title && <h3 className="text-base font-semibold">{title}</h3>}
          {description && <p className="mt-1 text-sm text-base-content/70">{description}</p>}
        </div>
        {action}
      </div>
    )}
    {children}
  </section>
)

export const EmptyState = ({
  title,
  children,
}: {
  title: string
  children?: ReactNode
}) => (
  <div className="rounded-box border border-dashed border-base-300 p-6 text-center">
    <p className="font-medium">{title}</p>
    {children && <div className="mt-2 text-sm opacity-70">{children}</div>}
  </div>
)

/** Inline before/after comparison used by the improver and the guides. */
export const BeforeAfter = ({
  before,
  after,
  note,
}: {
  before: string
  after: string
  note?: string
}) => (
  <div className="overflow-hidden rounded-box border border-base-300">
    <div className="border-b border-base-300 bg-base-200 p-3">
      <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-error">Before</p>
      <p className="text-sm line-through decoration-error/40">{before}</p>
    </div>
    <div className="p-3">
      <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-success">After</p>
      <p className="text-sm">{after}</p>
      {note && <p className="mt-2 text-xs opacity-70">{note}</p>}
    </div>
  </div>
)

export const Chip = ({
  children,
  tone = 'neutral',
  onClick,
  title,
}: {
  children: ReactNode
  tone?: 'neutral' | 'success' | 'warning' | 'error' | 'info'
  onClick?: () => void
  title?: string
}) => {
  /*
   * The fill is 5%, not 10%.
   *
   * A tinted chip is the one place in this UI where a semantic colour is drawn
   * on a background made from *itself*, and that quietly raises the contrast
   * floor: `text-success` clears 4.5:1 everywhere else on the site and measured
   * 3.98:1 inside its own 10% chip. Halving the fill puts the background back
   * near the surface and takes every tone past 4.5:1.
   *
   * Halving the fill rather than darkening the four semantic tokens is the
   * cheaper trade by a wide margin: those tokens are read by ~40 other call
   * sites that were already passing, and pushing them darker to rescue one
   * component would have dulled the whole status palette. The border stays at
   * 40% so the chip keeps its shape — a border is not text and carries no 4.5:1
   * obligation.
   */
  const toneClass =
    tone === 'success'
      ? 'border-success/40 bg-success/5 text-success'
      : tone === 'warning'
        ? 'border-warning/40 bg-warning/5 text-warning'
        : tone === 'error'
          ? 'border-error/40 bg-error/5 text-error'
          : tone === 'info'
            ? 'border-info/40 bg-info/5 text-info'
            : 'border-base-300 bg-base-200'

  const className = `inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-medium ${toneClass}`

  if (onClick) {
    return (
      <button type="button" className={`${className} cursor-pointer`} onClick={onClick} title={title}>
        {children}
      </button>
    )
  }
  return (
    <span className={className} title={title}>
      {children}
    </span>
  )
}
